import React, { useEffect } from "react";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { UseFormReturn } from "react-hook-form";
import { useIncrementOptions } from "../../hooks/useIncrementOptions";
import { AlternativePricingFields } from "./AlternativePricingFields";
import { ProgramPriceStateField } from "./ProgramPriceStateField";
import { programPriceStateOf } from "./programPriceState";
import { tierCountClears, type PricingClears } from "../schemas/programPricingClears";

export interface PricingFieldsProps {
  form: UseFormReturn<any>;
}

/** KB-4281: the tier-count clears are shared with the operator CLI (programPricingClears.ts). */
function applyPricingClears(form: UseFormReturn<any>, clears: PricingClears): void {
  for (const [field, value] of Object.entries(clears.fields)) form.setValue(field, value, { shouldDirty: true });
  const alts = form.getValues("regularOfferIncrementAlternatives") || [];
  alts.forEach((_: any, i: number) => {
    for (const f of clears.alternativeFields) form.setValue(`regularOfferIncrementAlternatives.${i}.${f}`, null, { shouldDirty: true });
  });
}

export const PricingFields: React.FC<PricingFieldsProps> = ({
  form
}) => {
  const {
    incrementOptions,
    loading
  } = useIncrementOptions();
  
  const pricingTiers = form.watch("pricingTiers") as any;
  const numTiers: 0 | 2 | 3 = pricingTiers?.numTiers === 2 ? 2 : pricingTiers?.numTiers === 3 ? 3 : 0;
  const useTieredPricing = numTiers > 0;
  // KB-4181 — a program declared Free or Price unknown carries no price: its price fields are cleared on
  // selection (applyProgramPriceState) and stay disabled until it is set back to Priced.
  const pricesDisabled = programPriceStateOf(form.watch("priceState")) !== null;

  // Track the selected increment option to conditionally show fields
  const selectedIncrement = form.watch("regularOfferIncrementKey");
  const isSingleDay = selectedIncrement === "oi_1day";
  
  // When increment changes to single day, reset single day option fields
  useEffect(() => {
    if (isSingleDay) {
      form.setValue("singleDayOption", false);
      form.setValue("singleDayPrice", null);
    }
  }, [isSingleDay, form]);
  
  console.log("[PricingFields] Rendering with increment options:", incrementOptions);
  
  return <div>
      <h3 className="text-lg font-semibold mb-4 text-left px-1">Session length and pricing</h3>
      
      <div className="space-y-6 mb-12">
        <ProgramPriceStateField form={form} />

        {/* 3-column layout with fixed-height label areas and fixed-height input areas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* First column - Session Length */}
          <div>
            <FormField control={form.control} name="regularOfferIncrementKey" render={({
            field
          }) => <FormItem>
                  {/* Fixed-height label container with bottom alignment */}
                  <div className="h-12 flex items-end">
                    <FormLabel className="mb-2">What is the regular length of each session?</FormLabel>
                  </div>
                  {/* Fixed-height input container */}
                  <div className="h-16">
                    <Select disabled={loading} onValueChange={value => {
                console.log("[PricingFields] Selected increment option:", value);
                field.onChange(value);
              }} value={field.value || "oi_none"}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select increment" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {loading ? <SelectItem value="oi_none">Loading...</SelectItem> : incrementOptions.map(option => <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </div>
                </FormItem>} />

            <FormField
              control={form.control}
              name="pricingTiers"
              render={() => (
                <FormItem className="mt-2">
                  <div className="flex items-center gap-1">
                    <FormControl>
                      <Checkbox
                        checked={useTieredPricing}
                        disabled={pricesDisabled}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            form.setValue("pricingTiers", {
                              v: 1,
                              numTiers: 2,
                              tier1Label: pricingTiers?.tier1Label ?? null,
                              tier2Label: pricingTiers?.tier2Label ?? null,
                              tier3Label: null
                            }, { shouldDirty: true });
                          } else {
                            form.setValue("pricingTiers", null, { shouldDirty: true });
                            applyPricingClears(form, tierCountClears(0));
                          }
                        }}
                      />
                    </FormControl>
                    <span className="text-sm text-slate-700">Use tiered pricing</span>

                    {useTieredPricing && (
                      <Select
                        value={String(numTiers)}
                        onValueChange={(v) => {
                          if (v === "2") {
                            form.setValue("pricingTiers", {
                              v: 1,
                              numTiers: 2,
                              tier1Label: pricingTiers?.tier1Label ?? null,
                              tier2Label: pricingTiers?.tier2Label ?? null,
                              tier3Label: null
                            }, { shouldDirty: true });
                            applyPricingClears(form, tierCountClears(2));
                          } else {
                            form.setValue("pricingTiers", {
                              v: 1,
                              numTiers: 3,
                              tier1Label: pricingTiers?.tier1Label ?? null,
                              tier2Label: pricingTiers?.tier2Label ?? null,
                              tier3Label: pricingTiers?.tier3Label ?? null
                            }, { shouldDirty: true });
                          }
                        }}
                      >
                        <FormControl>
                          <SelectTrigger className="h-8 w-[110px]">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="2">2 tiers</SelectItem>
                          <SelectItem value="3">3 tiers</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </FormItem>
              )}
            />
          </div>
          
          {/* Second column - Regular Price */}
          <div>
            <FormField control={form.control} name="priceRegular" render={({
            field
          }) => <FormItem>
                  {/* Fixed-height label container with bottom alignment */}
                  <div className="h-12 flex items-end">
                    <FormLabel className="mb-2">
                      {numTiers > 0
                        ? "What is the Tier 1 regular price for this session length? (Excluding add-ons and discounts)"
                        : "What is the regular price for this session length? (Excluding add-ons and discounts)"}
                    </FormLabel>
                  </div>
                  {/* Fixed-height input container */}
                  <div className="h-16">
                    <FormControl>
                      <Input type="number" placeholder="" {...field} disabled={pricesDisabled} value={field.value === null || field.value === undefined ? "" : field.value} onChange={e => {
                  const val = e.target.value === "" ? null : parseFloat(e.target.value);
                  console.log("[PricingFields] Regular price changed to:", val);
                  field.onChange(val);
                }} /> 
                    </FormControl>
                    <FormMessage />
                    <p className="text-sm text-muted-foreground mt-1">Price in US dollars</p>
                  </div>
                </FormItem>} />

            {numTiers >= 2 && (
              <FormField
                control={form.control}
                name="priceRegularTier2"
                render={({ field }) => (
                  <FormItem className="mt-4">
                    <FormLabel className="mb-2">Tier 2 regular price for this length</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder=""
                        {...field}
                        value={field.value === null ? "" : field.value}
                        onChange={e => {
                          const val = e.target.value === "" ? null : parseFloat(e.target.value);
                          field.onChange(val);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                    <p className="text-sm text-muted-foreground mt-1">Price in US dollars</p>
                  </FormItem>
                )}
              />
            )}

            {numTiers >= 3 && (
              <FormField
                control={form.control}
                name="priceRegularTier3"
                render={({ field }) => (
                  <FormItem className="mt-4">
                    <FormLabel className="mb-2">Tier 3 regular price for this length</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder=""
                        {...field}
                        value={field.value === null ? "" : field.value}
                        onChange={e => {
                          const val = e.target.value === "" ? null : parseFloat(e.target.value);
                          field.onChange(val);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                    <p className="text-sm text-muted-foreground mt-1">Price in US dollars</p>
                  </FormItem>
                )}
              />
            )}
          </div>
          
          {/* Third column - Price Footnote */}
          <div>
            <FormField control={form.control} name="priceNote" render={({
            field
          }) => <FormItem>
                  {/* Fixed-height label container with bottom alignment */}
                  <div className="h-12 flex items-end">
                    <FormLabel className="mb-2">Price footnote (optional)</FormLabel>
                  </div>
                  {/* Input container for textarea (needs more height) */}
                  <div>
                    <FormControl>
                      <Textarea placeholder="e.g., Sibling discounts available, early bird pricing, etc." className="min-h-[30px]" {...field} />
                    </FormControl>
                    <FormMessage />
                    <p className="text-sm text-muted-foreground mt-1">e.g., "Sibling discount available"</p>
                  </div>
                </FormItem>} />
          </div>
        </div>

        {useTieredPricing && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-md font-medium text-left">Price tier labels</h4>
              </div>
            </div>

            <div className="p-4 border rounded-lg bg-gray-50">
              <div className={`grid grid-cols-1 gap-4 ${numTiers === 3 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
                <FormField
                  control={form.control}
                  name="pricingTiers.tier1Label"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tier 1 label</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                          maxLength={60}
                        />
                      </FormControl>
                      <FormMessage />
                      <p className="text-sm text-muted-foreground mt-1">
                        This is the searchable price. It should be the price available to the general public.
                      </p>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="pricingTiers.tier2Label"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tier 2 label</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                          maxLength={60}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {numTiers === 3 && (
                  <FormField
                    control={form.control}
                    name="pricingTiers.tier3Label"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tier 3 label</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value)}
                            maxLength={60}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Alternative Pricing Fields - NEW. KB-4181: none for a Free / Price unknown program. */}
        {!pricesDisabled && <AlternativePricingFields form={form} />}

        {/* Only show these fields if not single day session */}
        {/* !isSingleDay && (
          <>
            <FormField control={form.control} name="singleDayOption" render={({
            field
          }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 mt-4">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={checked => {
                console.log("[PricingFields] Single day option changed to:", checked);
                field.onChange(checked);
              }} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      We also have a single-day option...
                    </FormLabel>
                  </div> 
                </FormItem>} />

            {form.watch("singleDayOption") && <FormField control={form.control} name="singleDayPrice" render={({
            field
          }) => <FormItem className="mt-1">
                    <FormLabel>What is the regular price for a single day? (Excluding add-ons and discounts)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="" 
                        className="max-w-[200px]" 
                        {...field} 
                        value={field.value === null ? "" : field.value} 
                        onChange={e => {
                          const val = e.target.value === "" ? null : parseFloat(e.target.value);
                          console.log("[PricingFields] Single day price changed to:", val);
                          field.onChange(val);
                        }} 
                      />
                    </FormControl>
                    <FormMessage />
                    <p className="text-sm text-muted-foreground mt-1">Price in US dollars</p>
                  </FormItem>} />}
          </>
        ) */}
      </div>
    </div>;
};
